import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageSEO from "@/components/feature/PageSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import {
  TYPE_DESC,
  TYPE_LABEL,
  filterAndSort,
  getByType,
  type FilterState,
} from "@/utils/movie";

const PAGE_SIZE = 12;
const TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function CategoryPage() {
  const { type = "all" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);

  const validType = type === "movie" || type === "tv" ? type : "all";

  const filters: FilterState = {
    region: searchParams.get("region") || "全部",
    genre: searchParams.get("genre") || "全部",
    year: searchParams.get("year") || "全部",
    sort: searchParams.get("sort") || "latest",
  };

  const base = useMemo(() => getByType(validType), [validType]);
  const result = useMemo(() => filterAndSort(base, filters), [base, filters]);

  useEffect(() => {
    setPage(1);
  }, [validType, filters.region, filters.genre, filters.year, filters.sort]);

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilter = (key: keyof FilterState, value: string) => {
    const next = new URLSearchParams(searchParams);
    const isDefault =
      (key === "sort" && value === "latest") || (key !== "sort" && value === "全部");
    if (isDefault) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleTypeChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    setSearchParams(next, { replace: true });
    navigate(`/category/${value}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
  };

  const resetFilters = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const typeName = validType === "all" ? "全部影视" : TYPE_LABEL[validType];
  const intro =
    validType === "all"
      ? "浏览熊猫影院收录的全部电影与电视剧，支持按地区、题材、年份与热度灵活筛选。"
      : TYPE_DESC[validType];

  return (
    <Layout>
      <PageSEO
        title={`熊猫影院-${typeName}影视作品列表`}
        description={`熊猫影院${typeName}列表，${intro} 支持多维筛选与分页浏览，快速找到想看的作品。`}
        keywords={`熊猫影院,${typeName},${typeName}列表,在线观看,影视资源`}
        path={`/category/${validType}`}
      />

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "影视分类", to: "/category/movie" },
            { label: typeName },
          ]}
        />

        <header className="mt-6">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {typeName}
            </h1>
            <span className="text-sm text-foreground-500">
              共 <strong className="text-primary-600">{result.length}</strong> 部作品
            </span>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">{intro}</p>
        </header>

        <div className="mt-6">
          <FilterBar
            typeOptions={TYPE_OPTIONS}
            currentType={validType}
            onTypeChange={handleTypeChange}
            filters={filters}
            onFilterChange={updateFilter}
            onReset={resetFilters}
          />
        </div>

        <div className="mt-8">
          {pageItems.length > 0 ? (
            <MovieGrid movies={pageItems} columns={5} />
          ) : (
            <EmptyState
              title="没有找到符合条件的影视作品"
              description="试试更换地区、题材或年份条件，或重置全部筛选后重新浏览。"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          )}
        </div>

        <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
      </div>
    </Layout>
  );
}
